import { useMemo, useEffect, useState } from 'react';

// react-table components
import {
  useTable,
  usePagination,
  useGlobalFilter,
  useAsyncDebounce,
  useRowSelect,
  useExpanded
} from 'react-table';

// @mui material components
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Icon from '@mui/material/Icon';
import Autocomplete from '@mui/material/Autocomplete';

// Material Dashboard 2 PRO React TS components
import {
  MDBox,
  MDTypography,
  MDInput,
  MDPagination,
  If,
  FaIcon
} from '@components/index';
import { parseJSON } from '@utils/common';

// Material Dashboard 2 PRO React TS examples components
import DataTableHeadCell from './DataTableHeadCell';
import DataTableBodyCell from './DataTableBodyCell';
import { Grid, IconButton, Theme, Tooltip } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import './styles.scss';
import CleanedTypeTable, { CleanedJSON } from './CleanedTypeTable';

// Declaring props types for DataTable
interface Props {
  entriesPerPage?:
    | false
    | {
        defaultValue: number;
        entries: number[];
      };
  canSearch?: boolean;
  showTotalEntries?: boolean;
  table: {
    columns: { [key: string]: any }[];
    rows: { [key: string]: any }[];
  };
  pagination?: {
    variant: 'contained' | 'gradient';
    color:
      | 'primary'
      | 'secondary'
      | 'info'
      | 'success'
      | 'warning'
      | 'error'
      | 'dark'
      | 'light';
  };
  noEndBorder?: boolean;
  onCheckboxSelection?: any;
  showEntriesPerPage?: boolean;
  stickyLastRow?: boolean;
  cleanedActionTable?: boolean;
  maxHeight?: string;
  customStyle?: any;
  cleanPageTable?: boolean;
  width?: string;
}

const CleanedActionRow = (row: any) => {
  const hasCleanedLabels = !!Object.keys(
    row?.row?.original?.cleaned_count || {}
  ).length;

  const [open, setOpen] = useState(false);

  const parentContainer = document.getElementById('clean-actions-modal');

  const handleButtonClick = () => {
    hasCleanedLabels && setOpen(!open);
  };

  const checkForEmptyData = (data: any) => {
    const parsedData = parseJSON<CleanedJSON>(String(Object.values(data)[0]));

    if (parsedData && parsedData.data.length === 0) return false;

    return true;
  };

  return (
    <>
      <TableRow {...row.row.getRowProps()}>
        {row?.row?.cells?.map((cell: any, key: any) => (
          <DataTableBodyCell
            key={key}
            align={cell.column.align ? cell.column.align : 'left'}
            {...cell.getCellProps()}
            noBorder={open ? true : false}
          >
            <If condition={cell.column.id === 'cleaned'}>
              <MDBox
                onClick={() => handleButtonClick()}
                sx={{ cursor: 'pointer' }}
              >
                {cell.render('Cell')}
              </MDBox>
            </If>
            <If condition={cell.column.id !== 'cleaned'}>
              {cell.render('Cell')}
            </If>
          </DataTableBodyCell>
        ))}
        <DataTableBodyCell noBorder={open ? true : false}>
          <IconButton
            onClick={handleButtonClick}
            sx={{
              height: '10px',
              pointerEvents: hasCleanedLabels ? 'auto' : 'none'
            }}
          >
            {hasCleanedLabels &&
              checkForEmptyData(row?.row?.original?.cleaned_types) &&
              (open ? (
                <FaIcon icon='angle-up' />
              ) : (
                <FaIcon icon='angle-down' />
              ))}
          </IconButton>
        </DataTableBodyCell>
      </TableRow>
      {open && (
        <CleanedTypeTable
          open={open}
          clientWidth={parentContainer?.clientWidth}
          data={row?.row?.original?.cleaned_types || []}
        />
      )}
    </>
  );
};

function DataTable({
  table,
  entriesPerPage = { defaultValue: 10, entries: [5, 10, 15, 20, 25] },
  canSearch = false,
  showTotalEntries = true,
  pagination = { variant: 'contained', color: 'dark' },
  noEndBorder = false,
  onCheckboxSelection,
  showEntriesPerPage,
  stickyLastRow = false,
  cleanedActionTable = false,
  maxHeight = '100%',
  customStyle,
  cleanPageTable = false,
  width
}: Props): JSX.Element {
  let defaultValue: any;
  let entries: any[] = [];

  if (entriesPerPage) {
    defaultValue = entriesPerPage.defaultValue
      ? entriesPerPage.defaultValue
      : '10';
    entries = entriesPerPage.entries
      ? entriesPerPage.entries
      : ['10', '25', '50', '100'];
  }

  const columns = useMemo<any>(() => table.columns || [], [table]);
  const data = useMemo<any>(() => table.rows, [table]);

  const tableInstance = useTable(
    { columns, data, initialState: { pageIndex: 0 } },
    useGlobalFilter,
    useExpanded,
    usePagination,
    useRowSelect
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    rows,
    page,
    pageOptions,
    canPreviousPage,
    canNextPage,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    setGlobalFilter,
    state: { pageIndex, pageSize, globalFilter },
    selectedFlatRows
  }: any = tableInstance;
  const tableWidth = document.getElementById('tableId')?.clientWidth || 1;

  // Set the default value for the entries per page when component mounts
  useEffect(() => setPageSize(defaultValue || 10), [defaultValue]);

  useEffect(() => {
    onCheckboxSelection?.(selectedFlatRows.map((row: any) => row.original));
  }, [selectedFlatRows.length]);

  // Set the entries per page value based on the select value
  const setEntriesPerPage = (value: any) => setPageSize(value);

  const [isMouseHovered, setIsMouseHovered] = useState(false);
  const [hoveredRow, setHoveredRow] = useState('');

  // Render the paginations
  const renderPagination = pageOptions.map((option: any) => (
    <MDPagination
      item
      key={option}
      onClick={() => gotoPage(Number(option))}
      active={pageIndex === option}
    >
      {option + 1}
    </MDPagination>
  ));

  // Handler for the input to set the pagination index
  const handleInputPagination = ({ target: { value } }: any) =>
    value > pageOptions.length || value < 0
      ? gotoPage(0)
      : gotoPage(Number(value));

  // Customized page options starting from 1
  const customizedPageOptions = pageOptions.map((option: any) => option + 1);

  // Setting value for the pagination input
  const handleInputPaginationValue = ({ target: value }: any) =>
    gotoPage(Number(value.value - 1));

  // Search input value state
  const [search, setSearch] = useState(globalFilter);

  // Search input state handle
  const onSearchChange = useAsyncDebounce((value: any) => {
    setGlobalFilter(value || undefined);
  }, 100);

  // Setting the entries starting point
  const entriesStart =
    pageIndex === 0 ? pageIndex + 1 : pageIndex * pageSize + 1;

  // Setting the entries ending point
  let entriesEnd;

  if (pageIndex === 0) entriesEnd = pageSize;
  else if (pageIndex === pageOptions.length - 1) entriesEnd = rows.length;
  else entriesEnd = pageSize * (pageIndex + 1);

  return (
    <TableContainer
      sx={{
        boxShadow: 'none',
        maxHeight,
        borderRadius: '0px',
        ...customStyle
      }}
    >
      {data?.length == 0 ? (
        <Grid
          sx={{
            flex: 1,
            textAlign: 'center',
            my: '21%'
          }}
        >
          <Grid item>
            <FontAwesomeIcon icon='warning' />
            <MDTypography variant='h4'>No Data Found</MDTypography>
          </Grid>
        </Grid>
      ) : (
        <>
          {(entriesPerPage && showEntriesPerPage) || canSearch ? (
            <MDBox
              display='flex'
              justifyContent='space-between'
              alignItems='center'
              p={3}
            >
              {entriesPerPage && showEntriesPerPage && (
                <MDBox display='flex' alignItems='center'>
                  <Autocomplete
                    disableClearable
                    value={pageSize.toString()}
                    options={entries}
                    onChange={(event, newValue) => {
                      setEntriesPerPage(parseInt(newValue, 10));
                    }}
                    size='small'
                    sx={{ width: '5rem' }}
                    renderInput={(params) => <MDInput {...params} />}
                  />
                  <MDTypography variant='sm' color='secondary'>
                    &nbsp;&nbsp;entries per page
                  </MDTypography>
                </MDBox>
              )}
              {canSearch && (
                <MDBox width='12rem' ml='auto'>
                  <MDInput
                    placeholder='Search...'
                    value={search}
                    size='small'
                    fullWidth
                    onChange={({ currentTarget }: any) => {
                      setSearch(search);
                      onSearchChange(currentTarget.value);
                    }}
                  />
                </MDBox>
              )}
            </MDBox>
          ) : null}
          <Table {...getTableProps()} id='tableId'>
            <MDBox
              component='thead'
              sx={({ palette: { dark }, borders: { borderWidth } }: Theme) => ({
                position: 'sticky',
                top: 0,
                background: 'white',
                outline: `${borderWidth[2]} solid ${dark.main}`,
                zIndex: 1
              })}
            >
              {headerGroups.map((headerGroup: any, key: any) => (
                <TableRow key={key} {...headerGroup.getHeaderGroupProps()}>
                  {headerGroup.headers.map((column: any, key: any) => (
                    <DataTableHeadCell
                      key={key}
                      width={column.width ? column.width : 'auto'}
                      align={column.align ? column.align : 'left'}
                    >
                      {column.render('Header')}
                    </DataTableHeadCell>
                  ))}
                </TableRow>
              ))}
            </MDBox>
            <TableBody {...getTableBodyProps()}>
              {page.map((row: any, key: any) => {
                prepareRow(row);

                return cleanedActionTable ? (
                  <CleanedActionRow row={row} key={row.key} />
                ) : (
                  <Tooltip
                    title={
                      cleanPageTable
                        ? Object.keys(row?.original?.dirt_types)?.length
                          ? ''
                          : 'No dirty data found for this column.'
                        : ''
                    }
                  >
                    <TableRow
                      key={key}
                      {...row.getRowProps()}
                      onMouseOver={() => {
                        setIsMouseHovered(true);
                        setHoveredRow(row.original.id);
                      }}
                      onMouseOut={() => {
                        setIsMouseHovered(false);
                        setHoveredRow('');
                      }}
                      className={
                        page.length - 1 === key && stickyLastRow
                          ? 'last-row'
                          : ''
                      }
                    >
                      {row.cells.map((cell: any, key: any) => (
                        <DataTableBodyCell
                          key={key}
                          noBorder={noEndBorder && rows.length - 1 === key}
                          align={cell.column.align ? cell.column.align : 'left'}
                          {...cell.getCellProps()}
                          width={width}
                        >
                          <If
                            condition={
                              cell.column.id !== 'hoverItem' && !cleanPageTable
                            }
                          >
                            {cell.render('Cell')}
                          </If>
                          <If condition={cleanPageTable}>
                            <MDBox
                              sx={{
                                opacity: cleanPageTable
                                  ? cell.column.id === 'status_icon'
                                    ? 1
                                    : Object.keys(row?.original?.dirt_types)
                                        ?.length
                                    ? 1
                                    : 0.5
                                  : 1
                              }}
                            >
                              {cell.render('Cell')}
                            </MDBox>
                          </If>
                          <If condition={cell.column.id === 'hoverItem'}>
                            <MDBox
                              opacity={
                                isMouseHovered &&
                                cell.row.original.id === hoveredRow
                                  ? 1
                                  : 0
                              }
                            >
                              {cell.render('Cell')}
                            </MDBox>
                          </If>
                        </DataTableBodyCell>
                      ))}
                    </TableRow>
                  </Tooltip>
                );
              })}
            </TableBody>
          </Table>
          {stickyLastRow && (
            <div
              style={{
                width: `${tableWidth - 1}px`,
                height: '2px',
                background: 'white',
                position: 'sticky',
                outline: '2px solid white',
                bottom: 0
              }}
            />
          )}

          <MDBox
            display='flex'
            flexDirection={{ xs: 'column', sm: 'row' }}
            justifyContent='space-between'
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            p={!showTotalEntries && pageOptions.length === 1 ? 0 : 3}
          >
            {showTotalEntries && (
              <MDBox mb={{ xs: 3, sm: 0 }}>
                <MDTypography
                  variant='button'
                  color='secondary'
                  fontWeight='regular'
                >
                  Showing {entriesStart} to {entriesEnd} of {rows.length}{' '}
                  entries
                </MDTypography>
              </MDBox>
            )}
            {pageOptions.length > 1 && (
              <MDPagination
                variant={pagination.variant ? pagination.variant : 'gradient'}
                color={pagination.color ? pagination.color : 'secondary'}
              >
                {canPreviousPage && (
                  <MDPagination item onClick={() => previousPage()}>
                    <Icon sx={{ fontWeight: 'bold' }}>chevron_left</Icon>
                  </MDPagination>
                )}
                {renderPagination.length > 6 ? (
                  <MDBox width='5rem' mx={1}>
                    <MDInput
                      inputProps={{
                        type: 'number',
                        min: 1,
                        max: customizedPageOptions.length
                      }}
                      value={customizedPageOptions[pageIndex]}
                      onChange={(event: any) => {
                        handleInputPagination(event);
                        handleInputPaginationValue(event);
                      }}
                    />
                  </MDBox>
                ) : (
                  renderPagination
                )}
                {canNextPage && (
                  <MDPagination item onClick={() => nextPage()}>
                    <Icon sx={{ fontWeight: 'bold' }}>chevron_right</Icon>
                  </MDPagination>
                )}
              </MDPagination>
            )}
          </MDBox>
        </>
      )}
    </TableContainer>
  );
}

export default DataTable;
